import _ from "lodash";
import { extractErrorMessage } from "@netapp/bxp-design-system-react"
import { setExternals } from "../../../utils/externals";
import { pauseAsyncPoller } from "utils/clients";
import { getItemAnon, setTenancyId, setOrgId, isNewTenancyEnabled } from "../../../utils/localstorage";
import { isAllowed } from "utils/permissions";
import { isNcl } from "utils/app/utils";
import { getTagValue, getScopeWithoutAdminAccess, getBoolean } from "modules/identity-access-management/utils";

const defaultState = () => ({
    orgStatus: "PENDING",
    accountStatus: "PENDING",
    workspaceStatus: "PENDING",
    agentStatus: "PENDING",
    adminOrgStatus: "PENDING",
    permissions: {},
    selectedOrg:{},
    whatsNew: false,
    blueXPShellLoaded: false,
    isSmtpConfigured: null
});

const derivePermissions = (selectedAccount) => {
    if (selectedAccount && selectedAccount.userRole === "Role-1") {
        return {
            addConnector: true,
            removeConnector: true,
            editAccount: true,
            allTabsAvailable: true,
            editConnectorUris: true
        }
    } else if (selectedAccount && (selectedAccount.userRole === 'Role-2')) {
        return {
            allTabsAvailable: true,
            removeConnector: true
        }
    } else if (selectedAccount && selectedAccount.userRole === "Role-4") {
        return {
            addConnector: true,
            allTabsAvailable: true,
            removeConnector: true
        }
    } else {
        return {}
    }
};

const hasAcessPermissions = (rules) => {
    return isAllowed(rules, "organization", "read")
           || isAllowed(rules, "project", "read")
           || isAllowed(rules, "folder", "read")
           || isAllowed(rules, "federaion", "read")
}

const hasAccountAcessPermissions = (scope) => {
    if (hasAcessPermissions(scope.rules)) {
        return true
    } else if (scope.childScopes) {
        for (const childScope of scope.childScopes) {
            if (hasAccountAcessPermissions(childScope)) {
                return true;
            }
        }
        return false
    }
    return true;
}

const deriveV4Permissions = (scopes, rules) => {
    const editAccount = hasAccountAcessPermissions(scopes);
    return {
        addConnector: isAllowed(rules, "agent", "create"),
        removeConnector: isAllowed(rules, "agent", "delete"),
        editAccount,
        editConnectorUris: isAllowed(rules, "agent", "modify"),
    }
};

const updateAccountRole = (accounts, orgAdmins) => {
    for (const account of accounts) {
        
        const org = _.find(orgAdmins, (org) => {
            const tag = _.find(org.tags, { "internal:bxp:legacyId": account.accountPublicId })
            return !_.isUndefined(tag);
        })
        
        if (org) {
            account.userRole = "Role-1";
        } else {
            account.userRole = "";
        }
    }
}

const getAccountDetails = (selectedOrg) => {
    const accountDetails = {
        accountPublicId: selectedOrg?.legacyId,
        accountName: selectedOrg?.name
    };
    if (selectedOrg?.tags) {
        for (const tag of selectedOrg?.tags) {
            if (tag["internal:bxp:saas"]) {
                accountDetails.isSaas = getBoolean(tag["internal:bxp:saas"])
            } else if (tag["internal:bxp:gov"]) {
                accountDetails.isGov = getBoolean(tag["internal:bxp:gov"])
            } else if (tag["internal:bxp:restricted"]) {
                accountDetails.isRestricted = getBoolean(tag["internal:bxp:restricted"])
            } else if (tag["internal:bxp:privatePreview"]) {
                accountDetails.isPrivatePreviewEnabled = getBoolean(tag["internal:bxp:privatePreview"])
            } else if (tag["internal:bxp:thirdPartyServices"]) {
                accountDetails.is3rdPartyServicesEnabled = getBoolean(tag["internal:bxp:thirdPartyServices"])
            } else if (tag["internal:bxp:serial"]) {
                accountDetails.accountSerial = tag["internal:bxp:serial"]
            }
        }
    }
    return accountDetails;
}

const demoAccounts = _.chain(process.env.REACT_APP_DEMO_ACCOUNTS).split(";").map(_.trim).reject(_.isEmpty).value();

const trimClients = (agentId) => {
    return agentId?.substring(0, agentId.length - "clients".length);
}

const updateAccount = (newAccount, state) => {
    const { selectedAccountId } = state;
    const accounts = state.accounts.map(account => {
        if (account.accountPublicId === selectedAccountId) {
            return newAccount;
        } else {
            return account;
        }
    });

    return { accounts, selectedAccount: newAccount }
}

const updateOrganization = (newOrganization, state) => {
    const { selectedOrganizationId } = state;
    const organizations = state.organizations.map(organization => {
        if (organization.id === selectedOrganizationId) {
            return newOrganization;
        } else {
            return organization;
        }
    });

    return { organizations, selectedOrganization: newOrganization }
}

export default (state = defaultState(), { type, payload, request }) => {
    const isTenancyV4 = isNewTenancyEnabled();
    switch (type) {
        case "APP:BOOT-SUCCESS": {
            if (process.env.REACT_APP_IS_SAAS === "false") {
                const { accountId, clientId } = payload;
                return { ...state, localAccountId: accountId, localConnectorId: `${clientId}clients` }
            } else {
                return state;
            }
        }
        // TODO to be cleaned up
        case "TENANCY:LOAD-ACCOUNTS-SUCCESS": {
            const { accounts, selectedAccountId, forbidden } = payload;
            const selectedAccount = _.find(accounts, { accountPublicId: selectedAccountId });

            const isDemo = _.includes(demoAccounts, selectedAccountId) || getItemAnon({ itemKey: `feature.demo-mode` });

            if (isDemo) {
                setExternals({ isDemo });
                pauseAsyncPoller();
            }

            setTenancyId({ tenancyId: selectedAccountId });
            return {
                ...state,
                accountStatus: "SUCCESS",
                orgStatus: "SUCCESS",
                isDemo,
                accounts: _.sortBy(accounts, [account => account.accountName.toLowerCase()]),
                selectedAccountId,
                selectedAccount,
                forbidden,
                permissions: derivePermissions(selectedAccount)
            }
        }
        case "TENANCY:SET-ACCOUNTS": {
            const { selectedAccount , selectedAccountId, forbidden, accounts} = payload;

            const isDemo = _.includes(demoAccounts, selectedAccountId) || getItemAnon({ itemKey: `feature.demo-mode` });

            setTenancyId({ tenancyId: selectedAccountId });
            const accs = _.isUndefined(accounts) ? state.accounts : accounts;
            const forbiddenValue = _.isUndefined(forbidden) ? state.forbidden : forbidden;
            return {
                ...state,
                accounts: accs,
                accountStatus: "SUCCESS",
                isDemo,
                selectedAccountId,
                selectedAccount,
                forbidden: forbiddenValue
            }
        }
        // end
        case "TENANCYV4:FETCH-USER-SUCCESS": {
            return {
                ...state,
                user: {...(state.user || {}), ...payload.user}
            }
        }
        case "TENANCYV4:SET-PERMISSIONS": {
            const { scopeId } = payload;
            const scopes = [state.selectedOrg, ...state.scopes]
            const rules = [...new Set(_.flatten(scopes.map(scope => scope.rules)))];
            const scope = _.find(scopes, { id: scopeId });
            return {
                ...state,
                permissions: scope ? deriveV4Permissions(state.selectedOrg, scope.rules) : state.permissions,
                rules: rules
            }
        }
        case "TENANCYV4:SET-ROLES-PERMISSIONS": {
            const { permissions } = payload;
            let roles = state.roles;
            const roleRuleMap = {};
            for (const permission of permissions) {
                for(const tag of permission.tags) {
                    if (tag["internal:bxp:roleId"]) {
                        if (!roleRuleMap[tag["internal:bxp:roleId"]]) {
                            roleRuleMap[tag["internal:bxp:roleId"]] = []
                        }
                        roleRuleMap[tag["internal:bxp:roleId"]].push(permission.rule)
                    }
                }
            }
            roles = roles.map(role => {
                role.rules = roleRuleMap[role.id];
                return role;
            });
            return {
                ...state,
                roles
            }
        }
        case "TENANCYV4:FETCH-USER-FAILED": {
            return {
                ...state,
                userError: extractErrorMessage(payload),
            }
        }
        case "TENANCYV4:FETCH-ROLES-SUCCESS": {
            return {
                ...state,
                roles: payload.roles,
                categories: payload.categories              
            }
        }
        case "TENANCYV4:FETCH-ROLES-FAILED": {
            return {
                ...state,
                rolesError: extractErrorMessage(payload),
            }
        }
        case "TENANCYV4:FETCH-ORGS-SUCCESS": {
            const { orgs, selectedOrgId, forbidden, orgsFullyLoaded, selectedScopeId, partnerOrgs, isSmtpConfigured } = payload;
            let selectedOrg = _.find(orgs, { id: selectedOrgId });
            if(_.isEmpty(selectedOrg)) {
                selectedOrg = _.find(partnerOrgs, { id: selectedOrgId });
            }
            const accounts = [];
            // TODO to be removed on scope and organization integration
            for (const org of orgs) {
                accounts.push(getAccountDetails(org))
            }

            if(state.selectedOrg?.rules) {
                selectedOrg.rules = state.selectedOrg?.rules
            }
            if(state.selectedOrg?.childScopes) {
                selectedOrg.childScopes = state.selectedOrg?.childScopes
            }
            const demoTag = !_.isUndefined(selectedOrg) ? _.find(selectedOrg.tags, { "internal:bxp:demo": "true" }) : undefined
            const isDemo = !_.isUndefined(demoTag) || _.includes(demoAccounts, selectedOrg?.legacyId) || getItemAnon({ itemKey: `feature.demo-mode` });

            if (isDemo) {
                setExternals({ isDemo });
                pauseAsyncPoller();
            }

            setOrgId({ orgId: selectedOrgId });
            setTenancyId({ tenancyId: selectedOrg?.legacyId });
            return {
                ...state,
                orgStatus: "SUCCESS",
                accountStatus: "SUCCESS",
                accounts,
                isDemo,
                orgs: _.sortBy(orgs, [org => org.name.toLowerCase()]),
                partnerOrgs: _.sortBy(partnerOrgs, [org => org.name.toLowerCase()]),
                selectedOrgId,
                selectedScopeId,
                selectedOrg,
                selectedAccount: getAccountDetails(selectedOrg),
                selectedAccountId: selectedOrg?.legacyId,
                forbidden,
                orgsFullyLoaded,
                ...(isSmtpConfigured !== undefined && { isSmtpConfigured })
            }
        }
        case "TENANCYV4:FETCH-ORGS-FAILED": {
            return {
                ...state,
                orgStatus: "FAILED",
                accountStatus: "FAILED",
            }
        }
        case "TENANCYV4:FETCH-ORGS-PENDING": {
            return {
                ...state,
                orgStatus: "PENDING",
            }
        }
        case "TENANCY:SET-SMTP-CONFIGURED": {
            return {
                ...state,
                isSmtpConfigured: payload
            }
        }
        case "TENANCYV4:LOAD-ADMIN-ORGS-SUCCESS": {
            const { adminOrgs } = payload;
            const accounts = state.accounts;
            updateAccountRole(accounts, adminOrgs)
            return {
                ...state,
                adminOrgStatus: "SUCCESS",
                adminOrgs,
                accounts
            }
        }
        case "TENANCYV4:LOAD-ADMIN-ORGS-FAILED": {
            return {
                ...state,
                adminOrgStatus: "FAILED",
            }
        }
        case "TENANCY:NO-AGENTS": {
            return {
                ...state,
                agentStatus: "NONE",
                blueXPShellLoaded: true,
            }
        }
        case "TENANCY/LOAD-FAILED": {
            return {
                ...state,
                accountStatus: "FAILED"
            }
        }

        // TODO to be cleaned up
        case "TENANCY:LOAD-WORKSPACES-SUCCESS": {
            const { workspaces, selectedWorkspaceId } = payload;
            const selectedWorkspace = _.find(workspaces, { workspacePublicId: selectedWorkspaceId });
            return {
                ...state,
                workspaceStatus: "SUCCESS",
                workspaces: _.sortBy(workspaces, [workspace => workspace.workspaceName.toLowerCase()]),
                selectedWorkspaceId,
                selectedWorkspace
            }
        }
        case "TENANCY:LOAD-WORKSPACES-FAILED": {
            return {
                ...state,
                workspaceStatus: "FAILED",
                workspaceErrorReason: extractErrorMessage(payload)
            }
        }
        case "TENANCY:SET-WORKSPACE": {
            let { workspaceId, workspace } = payload;
            const selectedWorkspace = _.isUndefined(workspace) ? _.find(state.workspaces, { workspacePublicId: workspaceId }) : workspace;
            return {
                ...state,
                workspaceStatus: "SUCCESS",
                selectedWorkspace,
                selectedWorkspaceId: workspaceId
            }
        }
        // end
        case "TENANCYV4:FETCH-SCOPES-SUCCESS": {
            const { scopes, selectedScopeId, scopesFullyLoaded } = payload;
            const selectedScope = _.find(scopes, { id: selectedScopeId });
            return {
                ...state,
                scopeStatus: "SUCCESS",
                workspaceStatus: "SUCCESS",
                scopesFullyLoaded: scopesFullyLoaded,
                scopes,
                selectedScopeId,
                selectedScope,
                selectedWorkspace: selectedScope ? {
                    workspacePublicId: selectedScope.legacyId,
                    workspaceName: selectedScope.name,
                    associatedAgents: selectedScope.associatedAgents,
                } : undefined,
                selectedWorkspaceId: selectedScope ? (isNcl() ? selectedScope.id : selectedScope.legacyId) : undefined,
                isLoading: false,
            }
        }
        case "TENANCYV4:FETCH-SCOPES-FAILED": {
            return {
                ...state,
                scopeStatus: "FAILED",
                workspaceStatus: "FAILED",
                scopeErrorReason: extractErrorMessage(payload),
                isLoading: false,
                blueXPShellLoaded: true,
            }
        }
        case "TENANCYV4:FETCH-SCOPES-LOADING": {
            return {
                ...state,
                isLoading: true,
            }
        }
        case "TENANCYV4:SET-SCOPE": {
            const { scopeId } = payload;
            const selectedScope = _.find(state.scopes, { id: scopeId });
            // connectorChanges: Remove this comment once the agent association is approved. 
            // const selectedAgent = (selectedScope?.associatedAgents?.length > 0) ? selectedScope?.associatedAgents[0] : undefined;
            // const selectedAgentId = (selectedScope?.associatedAgents?.length > 0) ? selectedScope?.associatedAgents[0].agentId : undefined;

            return {
                ...state,
                selectedScope,
                selectedScopeId: scopeId,
                // selectedAgent,
                // selectedAgentId,
                selectedWorkspace: {
                    workspacePublicId: selectedScope?.legacyId,
                    workspaceName: selectedScope?.name,
                    associatedAgents: selectedScope?.associatedAgents,
                },
                selectedWorkspaceId: selectedScope ? (isNcl() ? selectedScope.id : selectedScope.legacyId) : undefined,
            }
        }
        case "TENANCY:SET-AGENT-USER-SUCCESS": {
            const { agentUserId, agentId } = payload;
            let { userAgentMap } = state;
            if (_.isUndefined(userAgentMap)) {
                userAgentMap = {}
            }
            if (agentId && _.isUndefined(userAgentMap[agentId])) {
                userAgentMap[agentId] = agentUserId
            }
            return {
                ...state,
                agentStatus: "SUCCESS",
                userAgentMap,
                selectedAgentUserId: agentUserId,
            }
        }
        case "TENANCY:SET-AGENT-USER-FAILED": {
            return {
                ...state,
                agentStatus: "FAILED",
            }
        }
        case "TENANCY:LOAD-AGENTS-SUCCESS": {
            const { agents, selectedAgentId, isChangedAgent, isTransitionFromUnselectedToSelected } = payload;
            // connectorChanges: Remove this comment once the agent association is approved. 
            // const selectedAgent = _.find(isTenancyV4 ? state.selectedScope?.associatedAgents : agents, { agentId: selectedAgentId });
            const selectedAgent = _.find(agents, { agentId: selectedAgentId });

            return {
                ...state,
                agentStatus: isTenancyV4 && state.agentStatus !== "SUCCESS" ? "PENDING" : "SUCCESS",
                agents,
                selectedAgentId,
                selectedAgentIdNoClients: trimClients(selectedAgentId),
                selectedAgent,
                blueXPShellLoaded: true,
                isChangedAgent: state.isChangedAgent || isChangedAgent, //Won't set it to false if it is already true
                isTransitionFromUnselectedToSelected: state.isTransitionFromUnselectedToSelected || isTransitionFromUnselectedToSelected
            }
        }
        case "TENANCYV4:LOAD-AGENTS-SUCCESS": {
            const { scopes, selectedScopeId } = payload;
            const selectedScope = _.find(scopes, { id: selectedScopeId });
            return {
                ...state,
                scopes,
                selectedScopeId,
                selectedScope,
                selectedWorkspace: {
                    workspacePublicId: selectedScope.legacyId,
                    workspaceName: selectedScope.name,
                    associatedAgents: selectedScope.associatedAgents,
                },
                blueXPShellLoaded: true,
                selectedWorkspaceId: selectedScope.legacyId,
            }
        }
        case "TENANCY:DISCARD-CHANGE-AGENT-MESSAGE": {
            return { ...state, isChangedAgent: false, isTransitionFromUnselectedToSelected: false }
        }
        case "TENANCY:LOAD-AGENTS-FAILED": {
            console.log("Failed loading agents");
            console.log(payload);
            return {
                ...state,
                agentStatus: "FAILED",
                blueXPShellLoaded: true,
                agentErrorReason: extractErrorMessage(payload)
            }
        }
        case "TENANCY:SET-AGENT": {
            const { agentId, agentUserId } = payload;
            // connectorChanges: Remove this comment once the agent association is approved. 
            // const selectedAgent = _.find(isTenancyV4 ? state.tenancy.selectedScope?.associatedAgents : state.agents, { agentId: agentId });
            const selectedAgent = _.find(state.agents, { agentId: agentId });

            return {
                ...state,
                // For TenancyV4: only block UI if agentUserId couldn't be resolved
                agentStatus: (isTenancyV4 && !agentUserId) ? "PENDING" : "SUCCESS",
                selectedAgent,
                selectedAgentId: agentId,
                ...(agentUserId ? { selectedAgentUserId: agentUserId } : {}),
                blueXPShellLoaded: true,
                selectedAgentIdNoClients: trimClients(agentId)
            }
        }
        // TODO To be cleaned up
        case "TENANCYV4:CREATE-ORGANIZATION-PENDING": {
            return {
                ...state,
                creatingAccount: true
            }
        }
        case "TENANCYV4:CREATE-ORGANIZATION-SUCCESS": {
            return {
                ...state,
                creatingAccount: false    
            }
        }
        case "TENANCYV4:CREATE-ORGANIZATION-FAILED": {
            return {
                ...state,
                creatingAccount: false
            }
        }
        // end

        case "CONNECTORS:REMOVE-CONNECTOR-SUCCESS": {
            const { agentId } = payload;
            const agents = _.reject(state.agents, { agentId });
            return {
                ...state,
                agents
            }
        }
        case "CONNECTORS:UPDATE-URIS-SUCCESS": {
            const { agentId, redirectUris } = payload;
            const newPrimaryCallbackUri = _.find(redirectUris, "isDefault")?.value;

            const agents = _.map(state.agents, agent => {
                return agent.agentId === agentId ? { ...agent, primaryCallbackUri: newPrimaryCallbackUri || agent.automaticCallbackUris?.[0], manualOverrideUris: redirectUris } : agent;
            });

            return {
                ...state,
                agents
            }
        }
        // TODO to be removed
        case "TENANCY:ADD-WORKSPACE-SUCCESS": {
            const { workspaceName, workspacePublicId } = payload;
            const workspaces = _.sortBy([{ workspaceName, workspacePublicId }].concat(state.workspaces), [workspace => workspace.workspaceName.toLowerCase()]);

            return { ...state, workspaces }
        }
        case "TENANCY:DELETE-WORKSPACE-SUCCESS": {
            const { workspaceId } = payload;
            const workspaces = _.reject(state.workspaces, { workspacePublicId: workspaceId });

            if (workspaceId === state.selectedWorkspaceId) {
                return { ...state, workspaces, selectedWorkspace: workspaces[0], selectedWorkspaceId: workspaces[0].id }
            } else {
                return { ...state, workspaces }
            }
        }
        case "TENANCY:RENAME-WORKSPACE-SUCCESS": {
            const { newName, workspaceId } = payload;

            let newWorkspace = null;
            const workspaces = _.map(state.workspaces, workspace => {
                if (workspace.workspacePublicId === workspaceId) {
                    newWorkspace = { ...workspace, workspaceName: newName };
                    return newWorkspace;
                } else {
                    return workspace;
                }
            });

            if (workspaceId === state.selectedWorkspaceId && newWorkspace) {
                return { ...state, workspaces, selectedWorkspace: newWorkspace }
            } else {
                return { ...state, workspaces }
            }
        }
        case "TENANCY-RENAME-ACCOUNT-SUCCESS": {
            const { newName } = payload;
            const { selectedAccountId } = state;

            let newAccount = state.selectedAccount;
            const accounts = _.sortBy(state.accounts.map(account => {
                if (account.accountPublicId === selectedAccountId) {
                    newAccount = { ...account, accountName: newName };
                    return newAccount;
                } else {
                    return account;
                }
            }), [account => account.accountName.toLowerCase()]);

            return { ...state, accounts, selectedAccount: newAccount }
        }
        case "TENANCY-ACCOUNT-SAAS-ENABLED-TOGGLE-SUCCESS": {
            const { value } = payload;

            let newAccount = { ...state.selectedAccount, isSaas: value };
            return { ...state, ...updateAccount(newAccount, state) }
        }
        case "ACCOUNTS-MANAGEMENT:TOGGLE-PRIVATE-PREVIEW-SUCCESS": {
            const { payload: { value } } = request;

            let newAccount = { ...state.selectedAccount, isPrivatePreviewEnabled: value };
            return { ...state, ...updateAccount(newAccount, state) }
        }
        case "ACCOUNTS-MANAGEMENT:TOGGLE-ALLOW-THIRD-PARTY-SERVICES-SUCCESS": {
            const { payload: { value } } = request;

            let newAccount = { ...state.selectedAccount, is3rdPartyServicesEnabled: value };
            return { ...state, ...updateAccount(newAccount, state) }
        }
        case "TENANCYV4:UPDATE-ORGANIZATION": {
            const { name, id } = payload;
            const { orgs, selectedOrg } = state;
            const updatedSelectedOrg = {...selectedOrg }
            updatedSelectedOrg.name = name;
            const updatedOrg = orgs.map((org) => {
                if(org.id === id ) {
                    org.name = name;
                }
                return org;
            })
            return {
                ...state,
                selectedOrg: updatedSelectedOrg,
                orgs: updatedOrg
            }
        }
        case "TENANCYV4:UPDATE-SELECTED-ORG": {
            const { selectedOrg, orgs } = payload;            
            return {
                ...state,
                selectedOrg,
                ...(orgs !== undefined && { orgs }),
            }
        }
        case "TENANCYV4:AIDE-PERMISSIONS-SUCCESS": {
            const { orgs, selectedOrg, scopes, selectedOrgId, selectedScopeId } = payload;
            const rules = [...new Set(_.flatten(selectedOrg.rules))];
            const accounts = [];
            for (const org of orgs) {
                accounts.push(getAccountDetails(org))
            }
            setOrgId({ orgId: selectedOrgId });
            setTenancyId({ tenancyId: selectedOrg?.legacyId });
            return {
                ...state,
                scopes,
                permissions: deriveV4Permissions(selectedOrg, rules),
                rules,
                selectedOrgId,
                selectedOrg,
                selectedScopeId,
                selectedScope: selectedOrg
            }
        }

        case "TENANCYV4:UPDATE-WHATS-NEW": {
            return {
                ...state,
                whatsNew: true,
            }
        }

        case "TENANCYV4:FETCH-ORG-ADMINS-PENDING": {
            return {
                ...state,
                hasOrgAdmin: null,
                hasFetchOrgAdminFailed: false
            }
        }
        case "TENANCYV4:FETCH-ORG-ADMINS-FAILURE": {
            return {
                ...state,
                hasOrgAdmin: null,
                hasFetchOrgAdminFailed: true
            }
        }

        case "TENANCYV4:FETCH-ORG-ADMINS-SUCCESS": {
            return {
                ...state,
                hasOrgAdmin: payload.data
            }
        }
        case "TENANCY:CREATE-ORGANIZATION-ERROR": {
            return {
                ...state,
                errMessage: payload.errMessage
            }
        }
        case "TENANCY:SET-ORGANIZATION-SAFE-MODE-PENDING": {
            return {
                ...state,
                isLoading: true
            }
        }
        case "TENANCY:SET-ORGANIZATION-SAFE-MODE-COMPLETE": {
            return {
                ...state,
                isLoading: false
            }
        }

        case "TENANCYV4:SAFE-MODE-MODIFY-USER-ACCESS-PENDING": {
            return {
                ...state,
                isLoading: true
            }
        }
        case "TENANCYV4:SAFE-MODE-MODIFY-USER-ACCESS-COMPLETE": {
            return {
                ...state,
                isLoading: false
            }
        }
        case "TENANCYV4:USER-IS-ADMIN": {
            return {
                ...state,
                isAdmin: payload.isAdmin
            }
        }
        case "TENANCYV4:FETCH-USER-CMATIDS-SUCCESS": {            
            return {
                ...state,
                cmatIds: payload.cmatIds,
                cmatCompanyID: payload.cmatCompanyID                
            }
        }        
        case "TENANCYV4:GET-SERVICE-PERMISSIONS-START": {
            const { permissionTagForService } = payload;
            const newPermissionsByServicesMap = { [permissionTagForService]: { status: "PENDING" } }

            return { ...state, permissionsByServicesMap: newPermissionsByServicesMap };
        }
        case "TENANCYV4:GET-SERVICE-PERMISSIONS-SUCCESS": {
            const {permissionTagForService, rulesForSelectedScope} = payload;
            const newPermissionsByServicesMap = { [permissionTagForService]: { status: "SUCCESS", rulesForSelectedScope } }

            return { ...state, permissionsByServicesMap: newPermissionsByServicesMap };
        }
        case "TENANCYV4:UPDATE-ROLES-SUCCESS": {
            return {
                ...state,
                roles: payload.updateRoles
            }
        }
        default: {
            return state;
        }
    }
    //end
};